import { CheckCircle, AlertCircle, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";

const SubscriptionStatus = ({ subscriptions = [] , onRenew }) => {
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState(null);

  if (!subscriptions.length) {
    return (
      <div className="bg-white rounded-xl p-5 shadow text-gray-600 text-sm mb-6">
        {t("subscription_status.no_active")}
      </div>
    );
  }

  const toggleDetails = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const planMeta = {
    free: {
      label: t("subscription_status.plan_free", "رایگان"),
      className: "bg-gray-200 text-gray-700",
    },
    basic: {
      label: t("subscription_status.plan_basic", "پایه"),
      className: "bg-blue-200 text-blue-800",
    },
    pro: {
      label: t("subscription_status.plan_pro", "پیشرفته"),
      className: "bg-yellow-200 text-yellow-900",
    },
  };

  return (
    <div className="rounded-xl p-5 shadow text-white bg-gradient-to-r from-indigo-500 to-purple-700 mb-8">
      <h3 className="font-bold text-xl mb-4">
        {t("subscription_status.title")}
      </h3>
      {subscriptions.map((sub, idx) => {
        const isActive = sub.status === "active";
        const isExpanded = expandedIndex === idx;

        return (
          <div key={idx}>
            <div
              className="flex items-center justify-between py-2 cursor-pointer transition-colors duration-200 hover:bg-white/10 rounded-md p-2"
              onClick={() => toggleDetails(idx)}
            >
              <div className="flex items-center">
                {isActive ? (
                  <CheckCircle className="w-5 h-5 text-green-200 me-2" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-200 me-2" />
                )}
                <h4 className="font-semibold flex items-center gap-2">
                  {sub.categoryTitle ||
                    t("subscription_status.unknown_category")}

                  {/* ✅ Badge پلن */}
                  {sub.plan && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        planMeta[sub.plan?.toLowerCase()]?.className ||
                        "bg-gray-200 text-gray-700"
                      }`}
                    >
                      {planMeta[sub.plan?.toLowerCase()]?.label || sub.plan}
                    </span>
                  )}
                </h4>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-300 ${
                  isExpanded ? "rotate-180" : ""
                }`}
              />
            </div>

            {/* بخش جزئیات که با دراپ‌داون باز و بسته می‌شود */}
            <div
              className={`overflow-hidden transition-max-h duration-300 ease-in-out ${
                isExpanded ? "max-h-96" : "max-h-0"
              }`}
            >
              <div className="p-4 bg-white/5 rounded-md mt-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <p>
                    <strong>{t("subscription_status.status")}: </strong>
                    {isActive
                      ? t("subscription_status.active")
                      : t("subscription_status.expired")}
                  </p>

                  {sub.plan && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        planMeta[sub.plan?.toLowerCase()]?.className ||
                        "bg-gray-200 text-gray-700"
                      }`}
                    >
                      {planMeta[sub.plan?.toLowerCase()]?.label || sub.plan}
                    </span>
                  )}
                </div>
                <p>
                  <strong>{t("subscription_status.plan", "پلن")}: </strong>
                  {planMeta[sub.plan?.toLowerCase()]?.label || sub.plan || "-"}
                </p>
                <p>
                  <strong>{t("subscription_status.purchase_date")}: </strong>
                  {new Date(sub.purchaseDate).toLocaleDateString("fa-IR")}
                </p>
                <p>
                  <strong>{t("subscription_status.expiry_date")}: </strong>
                  {new Date(sub.endDate).toLocaleDateString("fa-IR")}
                </p>
                {/* <p>
                  <strong>{t("subscription_status.price")}: </strong>
                  {sub.price}
                </p> */}
                {sub.features && sub.features.length > 0 && (
                  <>
                    <p className="mt-2">
                      <strong>{t("subscription_status.features")}:</strong>
                    </p>
                    <ul className="list-disc list-inside mt-1">
                      {sub.features.map((feature, fIdx) => (
                        <li key={fIdx}>{feature}</li>
                      ))}
                    </ul>
                  </>
                )}
                {isActive && (
                  <button onClick={() => onRenew(sub)} className="bg-white text-indigo-600 font-semibold text-sm px-4 py-1 rounded-md mt-4 hover:bg-gray-100 transition">
                    {t("subscription_status.renew_button")}
                  </button>
                )}
              </div>
              {sub.plan?.toLowerCase() === "pro" && (
                <div className="mt-4 p-3 bg-indigo-900/20 border border-indigo-400/30 rounded-lg text-sm">
                  <p className="font-semibold text-indigo-100 mb-1">
                    محدودیت استفاده روزانه هوش مصنوعی
                  </p>

                  <p>
                    <strong>امروز: </strong>
                    {sub.usage?.aiDaily?.totalUsed ?? 0} / 5
                    <span className="text-indigo-200">
                      {" "}
                      ( {Math.max(
                        0,
                        5 - (sub.usage?.aiDaily?.totalUsed ?? 0)
                      )}{" "}
                      باقی مانده )
                    </span>
                  </p>

                  {/* ✅ اگر خواستی جزئیات فیچرها هم نشون بدی */}
                  {sub.usage?.aiDaily?.byFeature && (
                    <div className="mt-2 text-xs text-indigo-200">
                      <p className="mb-1 font-medium">جزئیات استفاده:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>
                          تحلیل کارنامه:{" "}
                          {sub.usage.aiDaily.byFeature.resultAnalysis ?? 0}
                        </li>
                        <li>
                          توضیح سوال:{" "}
                          {sub.usage.aiDaily.byFeature.questionExplain ?? 0}
                        </li>
                      </ul>
                    </div>
                  )}

                  {/* ✅ نمایش تاریخ ریست */}
                  {sub.usage?.aiDaily?.date && (
                    <p className="mt-2 text-xs text-indigo-300">
                      ریست شده در:{" "}
                      {new Date(sub.usage.aiDaily.date).toLocaleDateString(
                        "fa-IR"
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>

            {idx < subscriptions.length - 1 && (
              <hr className="my-2 border-t border-indigo-400" />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SubscriptionStatus;
